import React, { useState } from 'react'
import { optionMatches } from 'helpers/search'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import Button from 'components/button'
import Input from 'components/input'
import SelectMenu from 'components/select/components/menu'
import { MenuList } from 'components/menu/items'
import { PanelSwitch } from 'components/menu/panel'
import { Cross, Drag, Expand, Plus, TypeToAdd } from 'components/icons'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { IContextMenuState } from 'types'

const ADD_MENU_WIDTH = 260

/** Whether a list is already A to Z, so sorting it again goes Z to A. */
export const isAscending = (options: string[]): boolean =>
  options.length > 1 && options.every((option, index) => index === 0 || options[index - 1].localeCompare(option) <= 0)

// One option: a field to type its name into, a × inside its end to remove it, and, while the list can
// be reordered, a handle in the gutter on hover to drag it by (or move it from the keyboard).
const OptionRow: React.FC<{
  id: string
  index: number
  value: string
  sortable: boolean
  onChange: (value: string) => void
  onRemove: () => void
}> = ({ id, index, value, sortable, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !sortable
  })

  return (
    <div
      ref={setNodeRef}
      data-testid={`option-row-${index}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`group/option relative flex ${isDragging ? 'z-10' : ''}`}
    >
      {sortable && (
        <Button
          internalType="transparent"
          innerRef={setActivatorNodeRef}
          className={`absolute -left-3 top-1/2 h-6 w-3 -translate-y-1/2 rounded-sm p-0 text-faint opacity-0 cursor-grab touch-none hover:bg-transparent hover:text-body focus-visible:opacity-100 group-hover/option:opacity-100 active:cursor-grabbing [&_svg]:size-3.5 ${isDragging ? 'opacity-100' : ''}`}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <Drag />
        </Button>
      )}
      <Input
        aria-label={`Option ${index + 1}`}
        type="text"
        value={value}
        placeholder="Name"
        className="h-8 rounded-md pr-8"
        onChange={(event) => onChange(event.target.value)}
      />
      <Button
        internalType="transparent"
        aria-label={`Remove ${value || 'this option'}`}
        className="absolute top-1/2 right-1 size-6 -translate-y-1/2 rounded p-0 text-muted hover:bg-red/10 hover:text-red [&_svg]:size-3.5"
        onClick={onRemove}
      >
        <Cross />
      </Button>
    </div>
  )
}

interface ColumnManualOptionsProps {
  options: string[]
  onAdd: (value: string) => void
  onUpdate: (index: number, value: string) => void
  onRemove: (index: number) => void
  onMove: (from: number, to: number) => void
  // Distinct values the table already holds in this column that aren't options yet.
  suggestions?: string[]
  // Values picked from the column's own (suggestions): one at a time, or all of them from the list's Add all row.
  onAddMany: (values: string[]) => void
  expandOptionsViaCellEntry?: boolean
  onExpandOptionsChange?: (value: boolean) => void
  // Given, only the first this many show, fading out under a View all pill that calls onViewAll (Edit column
  // opens the whole list on a page of its own); Add option stays above, outside the list.
  previewLimit?: number
  onViewAll?: () => void
  // Given, only the options with this in them show (Edit column's Options page search). They can't be dragged
  // while it's set: a filtered list has gaps a dragged row would land in.
  filter?: string
}

// Over the foot of a list cut short to its first few, as they fade out: the way to the whole of it.
export const ViewAllPill: React.FC<{ count: number; onClick: () => void; className: string }> = ({
  count,
  onClick,
  className
}) => (
  <div className={`absolute inset-x-0 flex justify-center ${className}`}>
    <Button
      internalType="outline"
      className="h-7 gap-1.5 rounded-full border-divider bg-surface-raised px-3 text-[13px] font-medium text-body shadow-[0_1px_2px_rgb(0_0_0/0.06)] hover:bg-surface-hover [&_svg]:size-3.5 [&_svg]:text-muted"
      data-testid="view-all-options"
      onClick={onClick}
    >
      View all ({count.toLocaleString()})
      <Expand />
    </Button>
  </div>
)

// The hand-entered options of a select column: each option a field you can type into, with a × to
// remove it, dragged by its handle in the gutter to reorder. "Add option", above them, opens a
// list of the values already in the column, to pick from, with a search that adds what's typed when it
// isn't one of them. Edit column's, for a column being added and for one already made, on its panel and its Options
// page. The
// options themselves are controlled by the consumer, which decides whether an edit goes to an
// in-memory draft or straight to the API.
const ColumnManualOptions: React.FC<ColumnManualOptionsProps> = ({
  options,
  onAdd,
  onUpdate,
  onRemove,
  onMove,
  suggestions = [],
  onAddMany,
  expandOptionsViaCellEntry,
  onExpandOptionsChange,
  previewLimit,
  onViewAll,
  filter
}) => {
  const [addMenu, setAddMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  // A pill's id for the drag-and-drop: its place, which stays put while its text is typed into (an id
  // from the text would remount the pill, and lose the caret, on every key).
  const ids = options.map((_, index) => `option-${index}`)
  // The options to show, each with its place in the whole list, which is what an edit or a drag acts on: the
  // ones matching the search, every word in any order, as a cell's dropdown matches them.
  const matching = options
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => !filter || optionMatches(value, filter))
  // The first previewLimit, when there are more: the first of them, so a dragged or typed row is the same
  // row in the whole list.
  const cut = previewLimit !== undefined && matching.length > previewLimit
  const shown = cut ? matching.slice(0, previewLimit) : matching
  const shownIds = shown.map(({ index }) => ids[index])
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    onMove(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)))
  }

  return (
    <>
      {/* First, over the options: the whole row, as an option is, for what to press to add one. */}
      <div className={`flex items-center gap-1 px-3 ${shown.length > 0 ? 'pb-1' : 'pb-1.5'}`}>
        <Button
          internalType="outline"
          // Blue, a light border and blue text, so the way to add one stands out from the options under it.
          className="h-8 flex-1 justify-start gap-1.5 border-blue/30 px-3 text-[13px] font-medium text-blue hover:border-blue/50 hover:bg-really-light-blue hover:text-blue [&_svg]:size-4"
          data-testid="add-option"
          aria-haspopup="listbox"
          aria-expanded={addMenu.open}
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect()
            setAddMenu({
              open: true,
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`,
              right: 'auto',
              bottom: 'auto'
            })
          }}
        >
          <Plus />
          Add option
        </Button>
      </div>

      {shown.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={shownIds} strategy={verticalListSortingStrategy}>
            <div className="relative px-3 pb-1.5">
              <div
                className="flex flex-col gap-1"
                style={cut ? { maskImage: 'linear-gradient(to bottom, black 45%, transparent)' } : undefined}
              >
                {shown.map(({ value: option, index }) => (
                  <OptionRow
                    key={ids[index]}
                    id={ids[index]}
                    index={index}
                    value={option}
                    sortable={options.length > 1 && !filter?.trim()}
                    onChange={(value) => onUpdate(index, value)}
                    onRemove={() => onRemove(index)}
                  />
                ))}
              </div>
              {cut && onViewAll && <ViewAllPill count={options.length} onClick={onViewAll} className="bottom-1.5" />}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {onExpandOptionsChange && (
        <MenuList>
          <PanelSwitch
            label="Add options typed into cells"
            icon={<TypeToAdd />}
            checked={expandOptionsViaCellEntry !== false}
            onChange={onExpandOptionsChange}
          />
        </MenuList>
      )}

      {addMenu.open && (
        // The id is one the Menu's click-outside already treats as part of the panel.
        <SelectMenu
          id="select-options"
          menuState={addMenu}
          setMenuState={setAddMenu}
          options={suggestions.map((value) => ({ label: value, value }))}
          optionsSelected={[]}
          // Several values in a row: the list stays open, and each drops out of it as it's added.
          multiselect
          onOptionClick={(value) => onAddMany([value])}
          onAddAll={onAddMany}
          onCreateOption={(value) => onAdd(value)}
          searchable
          width={ADD_MENU_WIDTH}
          noOptionsMessage="Type a name to add it as an option."
        />
      )}
    </>
  )
}

export default ColumnManualOptions
